<template>
  <!-- Sem permissão para os indicadores: atalhos para o que o usuário pode usar -->
  <div v-if="!podePainel" class="boas-vindas">
    <h2 class="saudacao">{{ saudacao }}, {{ primeiroNome }}</h2>
    <p class="ck-subtitulo">{{ hojeExtenso }}. Escolha por onde começar:</p>
    <div class="atalhos">
      <router-link v-for="a in atalhos" :key="a.link" :to="a.link" class="atalho card">
        <i :class="a.icone"></i><span>{{ a.nome }}</span>
      </router-link>
    </div>
    <p v-if="!atalhos.length" class="text-muted">Seu usuário ainda não tem permissões. Fale com o administrador do sistema.</p>
  </div>
  <div v-else class="dash">
    <!-- Cabeçalho e filtros -->
    <div class="dash-topo">
      <div>
        <h2 class="saudacao">{{ saudacao }}, {{ primeiroNome }}</h2>
        <p class="ck-subtitulo mb-0">{{ hojeExtenso }} · dados de {{ nomeMes }} de {{ filtros.ano }}<span v-if="atualizado"> · atualizado às {{ atualizado }}</span></p>
      </div>
      <div class="filtros">
        <select v-model.number="filtros.mes" class="form-select" @change="carregar" aria-label="Mês">
          <option v-for="(m, i) in meses" :key="i" :value="i + 1">{{ m }}</option>
        </select>
        <select v-model.number="filtros.ano" class="form-select" @change="carregar" aria-label="Ano">
          <option v-for="a in anos" :key="a" :value="a">{{ a }}</option>
        </select>
        <select v-model.number="filtros.idfilial" class="form-select" @change="carregar" aria-label="Filial">
          <option :value="0">Todas as filiais</option>
          <option v-for="f in filiais" :key="f.id" :value="f.id">{{ f.text }}</option>
        </select>
        <button class="btn btn-outline-primary" @click="carregar" :disabled="carregando" title="Atualizar">
          <i class="bi bi-arrow-clockwise" :class="{ girando: carregando }"></i>
        </button>
      </div>
    </div>

    <div v-if="erro" class="alert alert-warning">{{ erro }}</div>

    <!-- KPIs -->
    <div class="kpis">
      <div class="kpi">
        <div class="kpi-icone" style="--c: var(--ck-primaria)"><i class="bi bi-people"></i></div>
        <div><span class="kpi-rot">Colaboradores ativos</span><strong>{{ k.colaboradores }}</strong></div>
      </div>
      <div class="kpi">
        <div class="kpi-icone" style="--c: var(--ck-sucesso)"><i class="bi bi-person-check"></i></div>
        <div><span class="kpi-rot">Trabalhando agora</span><strong>{{ k.trabalhando }}</strong>
          <small>{{ k.pausa }} em intervalo · {{ k.encerrou }} encerraram</small></div>
      </div>
      <div class="kpi">
        <div class="kpi-icone" style="--c: var(--ck-info)"><i class="bi bi-fingerprint"></i></div>
        <div><span class="kpi-rot">Marcações hoje</span><strong>{{ k.marcacoes_hoje }}</strong>
          <small>{{ k.marcacoes_mes }} no mês</small></div>
      </div>
      <div class="kpi">
        <div class="kpi-icone" style="--c: var(--ck-alerta)"><i class="bi bi-alarm"></i></div>
        <div><span class="kpi-rot">Atrasos no mês</span><strong>{{ k.atrasos_mes }}</strong>
          <small>{{ taxaPontualidade }}% de pontualidade</small></div>
      </div>
      <div class="kpi">
        <div class="kpi-icone" style="--c: var(--ck-perigo)"><i class="bi bi-calendar-x"></i></div>
        <div><span class="kpi-rot">Faltas no mês</span><strong>{{ k.faltas_mes }}</strong>
          <small>dias sem marcação ou abono</small></div>
      </div>
      <router-link to="/justificativas" class="kpi kpi-link">
        <div class="kpi-icone" style="--c: #7a5cc2"><i class="bi bi-exclamation-diamond"></i></div>
        <div><span class="kpi-rot">Pendências</span><strong>{{ k.justificativas_pendentes + k.inconsistencias }}</strong>
          <small>{{ k.justificativas_pendentes }} justificativas · {{ k.inconsistencias }} dias ímpares</small></div>
      </router-link>
    </div>

    <!-- Gráficos principais -->
    <div class="grade g-8-4">
      <section class="painel">
        <header><h3>Marcações por dia</h3><span class="ck-subtitulo">por canal de registro</span></header>
        <v-chart :option="optPorDia" autoresize class="grafico" />
      </section>
      <section class="painel">
        <header><h3>Pontualidade</h3><span class="ck-subtitulo">dias úteis: no horário, com atraso (+10 min) ou falta</span></header>
        <v-chart :option="optPontualidade" autoresize class="grafico" />
      </section>
    </div>

    <div class="grade g-6-6">
      <section class="painel">
        <header>
          <h3>Agora</h3>
          <div class="legenda-estado">
            <span class="ponto trabalhando"></span>Trabalhando
            <span class="ponto pausa"></span>Intervalo
            <span class="ponto encerrou"></span>Encerrou
            <span class="ponto ausente"></span>Sem marcação
          </div>
        </header>
        <ul class="lista-agora">
          <li v-for="p in agora" :key="p.id">
            <span :class="['ponto', p.estado]"></span>
            <span class="nome">{{ p.nome }}</span>
            <span class="grupo">{{ p.grupo }}</span>
            <span class="ultima">{{ p.ultima ? rotTipo(p.tipo) + ' ' + p.ultima : '—' }}</span>
          </li>
          <li v-if="!agora.length" class="vazio">Nenhum colaborador ativo.</li>
        </ul>
      </section>

      <section class="painel">
        <header>
          <h3>Últimas marcações</h3>
          <router-link to="/registrosdeponto" class="ver-tudo">Ver todas <i class="bi bi-arrow-right"></i></router-link>
        </header>
        <ul class="lista-ultimas">
          <li v-for="u in ultimas" :key="u.id">
            <div class="canal" :class="canal(u).classe" :title="canal(u).nome"><i :class="canal(u).icone"></i></div>
            <div class="info">
              <strong>{{ u.nome }}</strong>
              <small>{{ rotTipo(u.tipo) }} · {{ canal(u).nome }}<span v-if="u.nsr"> · NSR {{ u.nsr }}</span></small>
            </div>
            <div class="quando">
              <strong>{{ String(u.hora).substring(0, 5) }}</strong>
              <small>{{ dataCurta(u.data) }}</small>
            </div>
          </li>
          <li v-if="!ultimas.length" class="vazio">Nenhuma marcação registrada ainda.</li>
        </ul>
      </section>
    </div>

    <div class="grade g-4-4-4">
      <section class="painel">
        <header><h3>Horários de pico</h3><span class="ck-subtitulo">marcações por hora no mês</span></header>
        <v-chart :option="optPorHora" autoresize class="grafico pequeno" />
      </section>
      <section class="painel">
        <header><h3>Horas trabalhadas por grupo</h3><span class="ck-subtitulo">no mês, em horas</span></header>
        <v-chart :option="optGrupos" autoresize class="grafico pequeno" />
      </section>
      <section class="painel">
        <header><h3>Precisa de atenção</h3></header>
        <div v-if="!inconsistentes.length && !ranking.length" class="vazio tudo-certo">
          <i class="bi bi-check2-circle"></i> Nada pendente neste período.
        </div>
        <div v-if="inconsistentes.length" class="bloco-atencao">
          <span class="rot">Dias com marcação ímpar</span>
          <router-link v-for="(i, x) in inconsistentes" :key="'i' + x" class="linha-atencao"
                       :to="{ path: '/registrosdeponto', query: { colaborador: i.id } }">
            <span>{{ i.nome }}</span><span class="badge badge-suave">{{ i.data }}</span>
          </router-link>
        </div>
        <div v-if="ranking.length" class="bloco-atencao">
          <span class="rot">Mais atrasos no mês</span>
          <div v-for="(r, x) in ranking" :key="'r' + x" class="linha-atencao">
            <span>{{ r.nome }}</span><span class="badge text-bg-warning">{{ r.qt }}x</span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
//Eu sou o caminho, a verdade e a vida. Ninguém vem ao Pai senão por mim. (João 14:6);

import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, PieChart, LineChart } from 'echarts/charts'
import { TooltipComponent, LegendComponent, GridComponent, TitleComponent } from 'echarts/components'
import VChart from 'vue-echarts'
import axios from 'axios'
import util from '../util.js'

use([CanvasRenderer, BarChart, PieChart, LineChart, TooltipComponent, LegendComponent, GridComponent, TitleComponent])

const CORES = { app: '#13534a', whatsapp: '#3fae6a', tablet: '#f2b134', rh: '#2f6fb3' }
const TIPOS = { entrada: 'Entrada', pausa: 'Pausa', retorno: 'Retorno', saida: 'Saída' }

export default {
  name: 'DashboardRh',
  components: { VChart },
  data() {
    const hoje = new Date()
    return {
      meses: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
      anos: [hoje.getFullYear() - 2, hoje.getFullYear() - 1, hoje.getFullYear()],
      filtros: { mes: hoje.getMonth() + 1, ano: hoje.getFullYear(), idfilial: 0 },
      filiais: [],
      k: { colaboradores: 0, trabalhando: 0, pausa: 0, encerrou: 0, marcacoes_hoje: 0, marcacoes_mes: 0,
           faltas_mes: 0, atrasos_mes: 0, pontuais_mes: 0, inconsistencias: 0, justificativas_pendentes: 0 },
      porDia: { dias: [], app: [], whatsapp: [], tablet: [], rh: [] },
      porHora: [],
      grupos: { grupos: [], horas: [] },
      agora: [],
      ultimas: [],
      inconsistentes: [],
      ranking: [],
      carregando: false,
      erro: '',
      atualizado: '',
      timer: null,
    }
  },
  computed: {
    podePainel() { return util.pode('painel', this.$store.state.usuario) },
    atalhos() {
      const u = this.$store.state.usuario
      return [
        { nome: 'Registros de ponto', link: '/registrosdeponto', icone: 'bi bi-fingerprint', perm: 'registros' },
        { nome: 'Espelho de ponto', link: '/relespelhoponto', icone: 'bi bi-calendar3', perm: 'espelho' },
        { nome: 'Escalas e exceções', link: '/escalas', icone: 'bi bi-calendar-week', perm: 'escalas' },
        { nome: 'Justificativas', link: '/justificativas', icone: 'bi bi-journal-check', perm: ['justificativas', 'justificativas_aprovar'] },
        { nome: 'Colaboradores', link: '/colaboradores', icone: 'bi bi-people', perm: 'colaboradores' },
        { nome: 'Jornadas', link: '/jornadas', icone: 'bi bi-clock-history', perm: 'jornadas' },
        { nome: 'Feriados', link: '/feriados', icone: 'bi bi-calendar-heart', perm: 'feriados' },
        { nome: 'Arquivos fiscais', link: '/exportar', icone: 'bi bi-file-earmark-lock', perm: 'fiscal' },
        { nome: 'Usuários', link: '/usuarios', icone: 'bi bi-person-gear', perm: 'usuarios' },
      ].filter(a => util.pode(a.perm, u))
    },
    primeiroNome() {
      return String((this.$store.state.usuario || {}).nomeusuario || '').split(' ')[0] || 'bem-vindo'
    },
    saudacao() {
      const h = new Date().getHours()
      return h < 12 ? 'Bom dia' : (h < 18 ? 'Boa tarde' : 'Boa noite')
    },
    hojeExtenso() {
      return new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })
    },
    nomeMes() {
      return this.meses[this.filtros.mes - 1].toLowerCase()
    },
    pctNoHorario() {
      const t = this.k.pontuais_mes + this.k.atrasos_mes + this.k.faltas_mes
      return t ? Math.round(this.k.pontuais_mes * 100 / t) : 0
    },
    taxaPontualidade() {
      const t = this.k.pontuais_mes + this.k.atrasos_mes
      return t ? Math.round(this.k.pontuais_mes * 100 / t) : 100
    },
    optPorDia() {
      const serie = (nome, chave) => ({
        name: nome, type: 'bar', stack: 'total', data: this.porDia[chave], itemStyle: { color: CORES[chave] },
        barMaxWidth: 18, emphasis: { focus: 'series' },
      })
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { bottom: 0, icon: 'roundRect', itemWidth: 10, itemHeight: 10, textStyle: { color: '#5f6f69' } },
        grid: { left: 8, right: 8, top: 16, bottom: 36, containLabel: true },
        xAxis: { type: 'category', data: this.porDia.dias, axisTick: { show: false }, axisLine: { lineStyle: { color: '#e1e8e6' } }, axisLabel: { color: '#5f6f69' } },
        yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#eef2f1' } }, axisLabel: { color: '#5f6f69' } },
        series: [serie('Aplicativo', 'app'), serie('WhatsApp', 'whatsapp'), serie('Relógio itinerante', 'tablet'), serie('Incluídas pelo RH', 'rh')],
      }
    },
    optPontualidade() {
      const dados = [
        { name: 'Pontuais', value: this.k.pontuais_mes, itemStyle: { color: '#13534a' } },
        { name: 'Atrasos', value: this.k.atrasos_mes, itemStyle: { color: '#f2b134' } },
        { name: 'Faltas', value: this.k.faltas_mes, itemStyle: { color: '#c0392b' } },
      ]
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { bottom: 0, icon: 'circle', itemWidth: 9, textStyle: { color: '#5f6f69' } },
        title: { text: this.pctNoHorario + '%', subtext: 'dias no horário', left: 'center', top: '36%',
                 textStyle: { fontSize: 26, fontWeight: 700, color: '#15231f' }, subtextStyle: { color: '#5f6f69' } },
        series: [{ type: 'pie', radius: ['58%', '78%'], center: ['50%', '45%'], avoidLabelOverlap: true,
                   label: { show: false }, itemStyle: { borderColor: '#fff', borderWidth: 2 }, data: dados }],
      }
    },
    optPorHora() {
      return {
        tooltip: { trigger: 'axis', formatter: (p) => `${p[0].axisValue}h: ${p[0].value} marcações` },
        grid: { left: 4, right: 8, top: 12, bottom: 4, containLabel: true },
        xAxis: { type: 'category', data: [...Array(24).keys()].map(h => String(h).padStart(2, '0')), boundaryGap: false,
                 axisTick: { show: false }, axisLine: { lineStyle: { color: '#e1e8e6' } }, axisLabel: { color: '#5f6f69', interval: 3 } },
        yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#eef2f1' } }, axisLabel: { color: '#5f6f69' } },
        series: [{ type: 'line', smooth: true, symbol: 'none', data: this.porHora, lineStyle: { width: 2, color: '#13534a' },
                   areaStyle: { color: 'rgba(19, 83, 74, .12)' } }],
      }
    },
    optGrupos() {
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: (p) => `${p[0].name}: ${p[0].value} h` },
        grid: { left: 4, right: 16, top: 8, bottom: 4, containLabel: true },
        xAxis: { type: 'value', splitLine: { lineStyle: { color: '#eef2f1' } }, axisLabel: { color: '#5f6f69' } },
        yAxis: { type: 'category', data: this.grupos.grupos, inverse: true, axisTick: { show: false },
                 axisLine: { show: false }, axisLabel: { color: '#15231f', width: 110, overflow: 'truncate' } },
        series: [{ type: 'bar', data: this.grupos.horas, barMaxWidth: 16, itemStyle: { color: '#1b6f63', borderRadius: [0, 4, 4, 0] } }],
      }
    },
  },
  mounted() {
    if (!this.podePainel) return
    this.carregarFiliais()
    this.carregar()
    this.timer = setInterval(() => { if (!document.hidden) this.carregar(true) }, 60000)
  },
  beforeUnmount() {
    clearInterval(this.timer)
  },
  methods: {
    rotTipo(t) { return TIPOS[t] || 'Marcação' },
    dataCurta(iso) { return util.dataBR(iso).substring(0, 5) },
    canal(u) {
      if (u.fonte !== 'O') return { nome: 'RH', classe: 'rh', icone: 'bi bi-person-gear' }
      if (u.origem === 'whatsapp') return { nome: 'WhatsApp', classe: 'whatsapp', icone: 'bi bi-whatsapp' }
      if (u.origem === 'tablet' || u.coletor === '02') return { nome: 'Relógio itinerante', classe: 'tablet', icone: 'bi bi-tablet' }
      return { nome: 'Aplicativo', classe: 'app', icone: 'bi bi-phone' }
    },
    async carregarFiliais() {
      try {
        const r = await axios.get(util.servidor + '/controller/select2sql.php', {
          params: { idempresa: this.$store.state.usuario.idempresa, token: util.token(), tabela: 'tbfiliais' },
        })
        this.filiais = Array.isArray(r.data) ? r.data : []
      } catch (e) { this.filiais = [] }
    },
    async carregar(silencioso = false) {
      if (this.carregando) return
      this.carregando = true
      if (!silencioso) this.erro = ''
      try {
        const resp = await util.reqaxios([
          { parametro: 'op', valor: 'carregadash' },
          { parametro: 'idempresa', valor: this.$store.state.usuario.idempresa },
          { parametro: 'mes', valor: this.filtros.mes },
          { parametro: 'ano', valor: this.filtros.ano },
          { parametro: 'idfilial', valor: this.filtros.idfilial },
        ], 'post', util.servidor + '/controller/dashboard.php')
        const d = resp.data
        if (!d || d.erro || !d.kpis) {
          this.erro = (d && d.erro) || (typeof d === 'string' ? d.replace(/^E - /, '') : 'Não foi possível carregar o painel.')
          return
        }
        this.k = d.kpis
        this.porDia = d.por_dia
        this.porHora = d.por_hora
        this.grupos = d.horas_grupo
        this.agora = d.agora
        this.ultimas = d.ultimas
        this.inconsistentes = d.inconsistentes
        this.ranking = d.ranking_atraso
        this.atualizado = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      } catch (e) {
        if (!silencioso) this.erro = 'Falha de comunicação com o servidor.'
      } finally {
        this.carregando = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.dash-topo { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; }
.saudacao { font-size: 1.5rem; font-weight: 700; margin: 0 0 2px; }
.filtros { display: flex; gap: 8px; flex-wrap: wrap; }
.filtros .form-select { width: auto; min-width: 120px; }
.girando { display: inline-block; animation: gira 1s linear infinite; }
@keyframes gira { to { transform: rotate(360deg); } }

.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(168px, 1fr)); gap: 14px; margin-bottom: 16px; }
.kpi {
  background: #fff; border-radius: var(--ck-raio); box-shadow: var(--ck-sombra);
  padding: 16px; display: flex; gap: 14px; align-items: flex-start; color: inherit; text-decoration: none;
}
.kpi-link:hover { box-shadow: 0 0 0 2px var(--ck-primaria-suave), var(--ck-sombra); }
.kpi-icone {
  width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
  display: grid; place-items: center; font-size: 1.2rem;
  color: var(--c); background: color-mix(in srgb, var(--c) 12%, white);
}
.kpi-rot { display: block; font-size: .78rem; color: var(--ck-texto-2); font-weight: 600; }
.kpi strong { display: block; font-size: 1.65rem; font-weight: 700; line-height: 1.15; font-variant-numeric: tabular-nums; }
.kpi small { color: var(--ck-texto-2); font-size: .74rem; }

.grade { display: grid; gap: 14px; margin-bottom: 14px; }
.g-8-4 { grid-template-columns: 2fr 1fr; }
.g-6-6 { grid-template-columns: 1fr 1fr; }
.g-4-4-4 { grid-template-columns: repeat(3, 1fr); }
@media (max-width: 1100px) { .g-8-4, .g-6-6, .g-4-4-4 { grid-template-columns: 1fr; } }

.painel { background: #fff; border-radius: var(--ck-raio); box-shadow: var(--ck-sombra); padding: 16px 18px; min-width: 0; }
.painel header { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
.painel h3 { font-size: .98rem; font-weight: 700; margin: 0; }
.grafico { height: 300px; width: 100%; }
.grafico.pequeno { height: 220px; }
.ver-tudo { font-size: .8rem; font-weight: 600; text-decoration: none; }

.ponto { width: 9px; height: 9px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.ponto.trabalhando { background: var(--ck-sucesso); box-shadow: 0 0 0 3px rgba(30, 142, 90, .15); }
.ponto.pausa { background: var(--ck-acento); }
.ponto.encerrou { background: #8aa39c; }
.ponto.ausente { background: #d6dfdc; }
.legenda-estado { display: flex; align-items: center; gap: 6px; font-size: .74rem; color: var(--ck-texto-2); flex-wrap: wrap; }
.legenda-estado .ponto { margin-left: 8px; }

.lista-agora, .lista-ultimas { list-style: none; margin: 0; padding: 0; max-height: 330px; overflow-y: auto; }
.lista-agora li { display: grid; grid-template-columns: 14px 1fr auto 96px; gap: 10px; align-items: center; padding: 8px 4px; border-bottom: 1px solid #f0f4f3; font-size: .86rem; }
.lista-agora .nome { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 500; }
.lista-agora .grupo { color: var(--ck-texto-2); font-size: .76rem; }
.lista-agora .ultima { text-align: right; color: var(--ck-texto-2); font-size: .78rem; font-variant-numeric: tabular-nums; }

.lista-ultimas li { display: flex; align-items: center; gap: 12px; padding: 9px 4px; border-bottom: 1px solid #f0f4f3; }
.canal { width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center; flex-shrink: 0; color: #fff; }
.canal.app { background: #13534a; } .canal.whatsapp { background: #3fae6a; }
.canal.tablet { background: #f2b134; color: #4a3500; } .canal.rh { background: #2f6fb3; }
.info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.info strong { font-size: .86rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.info small, .quando small { color: var(--ck-texto-2); font-size: .75rem; }
.quando { text-align: right; display: flex; flex-direction: column; font-variant-numeric: tabular-nums; }

.vazio { color: var(--ck-texto-2); font-size: .85rem; padding: 16px 4px; text-align: center; border: 0 !important; display: block !important; }
.tudo-certo i { color: var(--ck-sucesso); font-size: 1.1rem; margin-right: 4px; }
.bloco-atencao { margin-bottom: 12px; }
.bloco-atencao .rot { display: block; font-size: .74rem; font-weight: 600; color: var(--ck-texto-2); text-transform: uppercase; letter-spacing: .05em; margin-bottom: 4px; }
.linha-atencao { display: flex; justify-content: space-between; align-items: center; padding: 6px 2px; font-size: .85rem; color: inherit; text-decoration: none; border-bottom: 1px solid #f0f4f3; }
a.linha-atencao:hover { color: var(--ck-primaria); }
.boas-vindas .atalhos { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; margin-top: 12px; }
.boas-vindas .atalho { display: flex; align-items: center; gap: 12px; padding: 16px; text-decoration: none; color: var(--ck-texto); font-weight: 600; }
.boas-vindas .atalho i { font-size: 1.4rem; color: var(--ck-primaria); }
.boas-vindas .atalho:hover { box-shadow: 0 0 0 2px var(--ck-primaria-suave), var(--ck-sombra); }
</style>
